import React from 'react';
import { useBlocker } from 'react-router';

export function useUnsavedChangesWarning(isDirty: boolean, onDiscard: () => void) {
  const blocker = useBlocker(isDirty);
  const pendingProceedRef = React.useRef<(() => void) | null>(null);
  const [isTabPromptOpen, setIsTabPromptOpen] = React.useState(false);

  React.useEffect(() => {
    if (!isDirty) return;

    function onBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
      event.returnValue = '';
    }

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const confirmIfDirty = React.useCallback(
    (proceed: () => void) => {
      if (!isDirty) {
        proceed();
        return;
      }
      pendingProceedRef.current = proceed;
      setIsTabPromptOpen(true);
    },
    [isDirty]
  );

  const keepEditing = React.useCallback(() => {
    pendingProceedRef.current = null;
    setIsTabPromptOpen(false);
    blocker.reset?.();
  }, [blocker]);

  const discard = React.useCallback(() => {
    const proceed = pendingProceedRef.current;
    pendingProceedRef.current = null;
    setIsTabPromptOpen(false);
    onDiscard();
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
    proceed?.();
  }, [blocker, onDiscard]);

  return {
    confirmIfDirty,
    keepEditing,
    discard,
    isPromptOpen: blocker.state === 'blocked' || isTabPromptOpen,
  };
}
